import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useAbility, useIsReadOnly } from '@/lib/casl'
import { useFormContext } from '@/lib/form-contexts'
import { useStore } from '@tanstack/react-form'
import { Link } from '@tanstack/react-router'
import { ExternalLink } from 'lucide-react'
import CollectionStorefrontVisibilityIndicator from './CollectionStorefrontVisibilityIndicator'
import {
  applyCollectionVisibilityUpdates,
  getCollectionStorefrontVisibilityIssue,
  getStorefrontProductCount,
  updateCollectionVisibilityDraft,
} from './designForm.utils'
import NavigationMenuList from './NavigationMenuList'
import NavigationVisibilityControl from './NavigationVisibilityControl'
import {
  NAVIGATION_ITEM_TYPE,
  NAVIGATION_VISIBILITY_MODE,
  navigationItemLabel,
} from './navigationMenuEditor.utils'

export default function AutomaticNavigationMenuList({
  items,
  collections,
  onChange,
  visibleItemIds,
}) {
  const form = useFormContext()
  const ability = useAbility()
  const path = usePathHelper()
  const { isReadOnly } = useIsReadOnly()
  const collectionVisibilityUpdates =
    useStore(form.store, (state) => state.values.collection_visibility_updates) ?? []
  const collectionsById = new Map(
    applyCollectionVisibilityUpdates(collections, collectionVisibilityUpdates).map((collection) => [
      Number(collection.id),
      collection,
    ])
  )
  const canEditCollections = ability.can('edit', 'collections')
  const canViewCollections =
    ability.can('view', 'collections') || ability.can('list', 'collections')

  const setCollectionVisibility = (item, isPublished) => {
    const collectionId = item.collection_id
    const collection = collections.find(
      (candidate) => Number(candidate.id) === Number(collectionId)
    )
    if (!collection || isReadOnly || !canEditCollections) return

    if (isPublished && item.is_enabled === false) {
      setItemVisibility(item.id, true)
    }

    form.setFieldValue(
      'collection_visibility_updates',
      updateCollectionVisibilityDraft(
        collections,
        collectionVisibilityUpdates,
        collectionId,
        isPublished
      ),
      { touch: true }
    )
  }

  const setItemVisibility = (itemId, isEnabled, { manualOverride = false } = {}) => {
    if (isReadOnly) return

    onChange(
      items.map((item) =>
        String(item.id) === String(itemId)
          ? {
              ...item,
              is_enabled: isEnabled,
              props: manualOverride
                ? {
                    ...item.props,
                    visibility_mode: NAVIGATION_VISIBILITY_MODE.MANUAL,
                  }
                : item.props,
            }
          : item
      )
    )
  }

  const renderItemMeta = ({ item, collection, details }) => {
    if (item.type !== NAVIGATION_ITEM_TYPE.COLLECTION) {
      return <div className="text-xs text-muted-foreground">Menu link</div>
    }

    const visibilityIssue = getCollectionStorefrontVisibilityIssue(collection)

    return (
      <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
        <span>{details.label}</span>
        {visibilityIssue ? (
          <CollectionStorefrontVisibilityIndicator visibilityIssue={visibilityIssue} />
        ) : null}
        {canViewCollections && collection?.id ? (
          <Link
            {...path.collections.show(collection.id).link}
            className="inline-flex items-center gap-1 text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            Edit collection <ExternalLink className="size-3" />
          </Link>
        ) : null}
      </div>
    )
  }

  const renderItemEnd = ({ item, collection }) => {
    const label = navigationItemLabel(item, collectionsById)

    if (item.type !== NAVIGATION_ITEM_TYPE.COLLECTION) {
      return (
        <NavigationVisibilityControl
          checked={item.is_enabled !== false}
          disabled={isReadOnly}
          itemLabel={label}
          onCheckedChange={(checked) =>
            setItemVisibility(item.id, checked, {
              manualOverride: [
                NAVIGATION_ITEM_TYPE.SHOP_ALL,
                NAVIGATION_ITEM_TYPE.OCCASIONS,
              ].includes(item.type),
            })
          }
        />
      )
    }

    const hasProducts = Number(getStorefrontProductCount(collection) ?? 0) > 0
    const isPublished = collection?.is_published !== false
    const disabled = isReadOnly || !canEditCollections || !hasProducts
    const switchControl = (
      <NavigationVisibilityControl
        checked={hasProducts && isPublished && item.is_enabled !== false}
        disabled={disabled}
        itemLabel={label}
        scope="website"
        onCheckedChange={(checked) => setCollectionVisibility(item, checked)}
      />
    )

    if (!hasProducts || (!canEditCollections && !isReadOnly)) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{switchControl}</TooltipTrigger>
          <TooltipContent variant="warning" className="max-w-64">
            {!hasProducts
              ? 'Add a visible product before this collection can appear.'
              : 'You don’t have permission to change collection visibility.'}
          </TooltipContent>
        </Tooltip>
      )
    }

    return switchControl
  }

  return (
    <NavigationMenuList
      items={items}
      collectionsById={collectionsById}
      onReorder={onChange}
      allowItemChanges={false}
      getItemClassName={({ item, collection }) => {
        const isMuted =
          item.type === NAVIGATION_ITEM_TYPE.COLLECTION
            ? item.is_enabled === false ||
              getCollectionStorefrontVisibilityIssue(collection) !== null
            : item.is_enabled === false

        return isMuted ? 'bg-muted/30 text-muted-foreground' : null
      }}
      renderItemEnd={renderItemEnd}
      renderItemMeta={renderItemMeta}
      showCollectionVisibilityIndicator={false}
      visibleItemIds={visibleItemIds}
    />
  )
}
